import { useEffect, useRef, useState } from 'react';
import Button from '../atoms/Button';

interface PaymentFailedModalProps {
  open: boolean;
  txHash?: string | null;
  onRetry: () => void;
  onClose: () => void;
}

export function PaymentFailedModal({ open, txHash, onRetry, onClose }: PaymentFailedModalProps) {
  const retryRef = useRef<HTMLButtonElement>(null);
  const [queryHash, setQueryHash] = useState(txHash ?? '');

  useEffect(() => {
    if (!open) {
      setQueryHash(txHash ?? '');
      return;
    }
    retryRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, txHash]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-failed-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/40 px-6 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-lg border border-hairline bg-paper p-8 shadow-2xl animate-modalIn">
        <div className="rounded border border-vermilion-100 bg-vermilion-50 p-4">
          <div className="flex gap-3">
            <span className="grid h-8 w-8 place-items-center rounded bg-vermilion text-ink-inv font-mono text-sm">!</span>
            <div>
              <h2 id="payment-failed-title" className="font-serif text-2xl font-medium text-vermilion-700">
                未收到 5 USDT
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-vermilion-700">
                超过链上确认窗口；前次资源已释放，可重新发起。
              </p>
            </div>
          </div>
        </div>

        <h3 className="mt-6 text-sm font-semibold text-ink">可能原因</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-ink-2">
          <li>错链：选择 TRC-20 但转入 ERC-20，或反向。</li>
          <li>错额：金额不是 5 USDT，且超出容差范围。</li>
          <li>24h 超时：转账广播后未在窗口内完成确认。</li>
          <li>tx 未广播：钱包未真正发出交易。</li>
        </ul>

        <div className="mt-5 rounded border border-hairline bg-canvas p-4">
          <label className="block font-mono text-xs uppercase tracking-hud text-ink-2" htmlFor="tx-hash">
            tx_hash
          </label>
          <input
            id="tx-hash"
            value={queryHash}
            onChange={(e) => setQueryHash(e.target.value)}
            placeholder="a8f3..."
            className="mt-2 w-full rounded border border-hairline bg-paper px-3 py-2 font-mono text-xs text-ink outline-none focus:border-jade"
          />
          {queryHash && (
            <div className="mt-3 inline-flex max-w-full items-center rounded border border-hairline bg-paper px-3 py-1 font-mono text-xs text-ink-2">
              <span className="truncate">{queryHash}</span>
            </div>
          )}
        </div>

        <div className="mt-6 grid gap-2">
          <Button ref={retryRef} variant="primary" onClick={onRetry}>
            重新发起支付
          </Button>
          <Button variant="secondary" onClick={onClose}>
            回到首页
          </Button>
        </div>
      </div>
    </div>
  );
}
